import {
  InputSelect,
  Item,
} from '../../../../../shared/components/InputSelect';
import { TabPanel } from '@mui/lab';
import { InputDate } from '../../../../../shared/components/InputDate';
import { Button, Checkbox, FormControlLabel, Tooltip, Typography } from '@mui/material';
import { InputMoney } from '../../../../../shared/components/InputMoney';
import Box from '@mui/material/Box';
import DataTable from '../../../../../shared/components/Table';
import { GridColDef } from '@mui/x-data-grid';
import { useEffect, useState } from 'react';
import DevolucaoController from '../../../../../controllers/DevolucaoController';
import ClienteController from '../../../../../controllers/ClienteController';
import { formatCurrency } from '../../../../../shared/utils/helpers/numbers';

const OPTIONS = [
  {
    value: 1,
    label: 'Master Card',
  },
  {
    value: 2,
    label: 'Visa',
  },
];

const COLUMNS: GridColDef[] = [
  {
    field: 'cc_descricao',
    headerName: 'Centro de Custo',
    headerAlign: 'left',
    align: 'left',
    headerClassName: '!w-[50%]',
    valueGetter: (value: any) => value,
  },
  {
    field: 'percentual',
    headerName: 'Percentual %',
    headerAlign: 'left',
    align: 'left',
    headerClassName: '!w-[50%]',
    valueGetter: (value: any) => value,
  },
];

// As larguras usavam headerClassName ('!w-[20%]'), que dimensiona SO o cabecalho -
// a celula nao acompanhava e o titulo ficava desalinhado do conteudo. Com flex o
// DataGrid aplica a mesma proporcao ao cabecalho e a celula.
/* [29/09] As notas de devolução desta venda.
 *
 * Só número e valor, como o card pede. A data entra porque uma venda com duas devoluções
 * fica ilegível sem ela - e cabe na largura que sobrou. */
const DEVOLUCAO_COLUMNS: GridColDef[] = [
  {
    field: 'numero',
    headerName: 'Nota',
    headerAlign: 'left',
    align: 'left',
    flex: 1,
    valueGetter: (value: any) => value ?? '',
  },
  {
    field: 'data',
    headerName: 'Data',
    headerAlign: 'left',
    align: 'left',
    flex: 1,
    valueGetter: (value: any) =>
      value ? String(value).substring(0, 10).split('-').reverse().join('/') : '',
  },
  {
    field: 'valor',
    headerName: 'Valor',
    headerAlign: 'right',
    align: 'right',
    flex: 1,
    valueGetter: (value: any) => formatCurrency(Number(value ?? 0)),
  },
];

const PARCELAS_COLUMNS: GridColDef[] = [
  {
    field: 'parcela_numero',
    headerName: 'Parcela',
    headerAlign: 'left',
    align: 'left',
    flex: 1,
    valueGetter: (value: any) => value,
  },
  {
    field: 'data_vencimento',
    headerName: 'Vencimento',
    headerAlign: 'left',
    align: 'left',
    flex: 2,
    valueGetter: (value: any) =>
      value ? String(value).substring(0, 10).split('-').reverse().join('/') : '',
  },
  {
    field: 'valor',
    headerName: 'Valor',
    headerAlign: 'left',
    align: 'left',
    flex: 2,
    valueGetter: (value: any) =>
      value != null
        ? Number(value).toLocaleString('pt-BR', {
            style: 'currency',
            currency: 'BRL',
          })
        : '',
  },
];

interface TabFinanceiroProps {
  paymentOptions: Item[];
  paymentConditionOptions: Item[];
  costCenterOptions: Item[];
  values: any;
  handleChange: any;
  handleBlur: any;
  touched: any;
  errors: any;
  setFieldValue: any;
  duploClick?: boolean;
  temFormaPagamentoSelecionada?: boolean;
  // Veredito do servidor sobre reprocessar os boletos desta venda.
  reprocessarBoleto?: {
    podeReprocessar: boolean;
    motivo?: string;
    qtdBoletos?: number;
  } | null;
  reprocessandoBoleto?: boolean;
  handleReprocessarBoletoVenda?: () => void;
  // [29/09] Para buscar as notas de devolução desta venda. Ausente na venda nova.
  vendaId?: number | null;
  // [30/09] Boleto vivo: o que foi cobrado nao muda (ver usePedidoDetalhes).
  travadoPorBoleto?: boolean;
}

export const TabFinanceiro = ({
  paymentOptions,
  paymentConditionOptions,
  errors,
  touched,
  values,
  handleChange,
  handleBlur,
  costCenterOptions,
  setFieldValue,
  duploClick,
  temFormaPagamentoSelecionada,
  reprocessarBoleto,
  reprocessandoBoleto,
  handleReprocessarBoletoVenda,
  vendaId,
  travadoPorBoleto,
}: TabFinanceiroProps) => {
  /* [30/09] Tudo o que vira titulo trava com boleto vivo. O botao Gerar boleto NAO usa
   * isto: ele tem regra propria, e e o caminho de volta depois de cancelar os boletos. */
  const travado = !!duploClick || !!travadoPorBoleto;

  /* [29/09] O grid de devoluções e os dois totalizadores novos.
   *
   * Buscado aqui, e não junto com a venda: é informação de OUTRO documento, que nasce
   * depois dela e muda sem que a venda mude. Carregar junto obrigaria a recarregar a venda
   * inteira para atualizar um grid de leitura.
   *
   * Venda nova (sem id) não busca nada. */
  const [notasDevolucao, setNotasDevolucao] = useState<any[]>([]);

  useEffect(() => {
    if (!vendaId) return;

    let ativo = true;

    DevolucaoController.NotasDaVenda(vendaId).then((resposta) => {
      if (!ativo) return;

      /* O `id` é OBRIGATÓRIO: o DataGrid do MUI exige que toda linha o tenha, e o
         DataTable deste projeto não define getRowId. Sem ele o componente LANÇA, e a guia
         Financeiro inteira vira tela branca - foi o que aconteceu em 28/09, na venda 87.
         As linhas de parcela já vinham com id; as daqui vêm com nfe_id. */
      const linhas = Array.isArray(resposta.Data) ? resposta.Data : [];

      setNotasDevolucao(linhas.map((n: any) => ({ ...n, id: n?.nfe_id })));
    });

    // Evita gravar resposta de uma venda que já não está na tela.
    return () => { ativo = false; };
  }, [vendaId]);

  /* A soma sai das MESMAS linhas que o grid mostra - é o que impede o totalizador de
   * discordar da lista. (Diferente do extrato da conta corrente, onde o acumulado por
   * linha é cálculo de verdade e por isso vem do banco.) */
  const totalDevolucao = notasDevolucao.reduce(
    (soma, n) => soma + Number(n?.valor ?? 0), 0);

  /* O InputMoney recebe STRING - ele mesmo formata como moeda (NumericFormat). Os outros
     totalizadores vêm assim do formulário; estes dois são calculados, então a conversão
     acontece aqui. toFixed(2) e não String(): sem ele um valor como 1200.005 chegaria com
     casas que o campo não espera. */
  const comoTexto = (n: number) => n.toFixed(2);

  /* O Total Geral é da VENDA e não muda com a devolução - decisão do card. O SubTotal é o
   * que sobra. Aparece sempre, mesmo sem devolução: tela que muda de forma conforme o dado
   * faz o operador procurar um campo que ele viu ontem. */
  /* [30/09] O credito tambem sai do que sobra - pedido do Deivid. Sem ele, o SubTotal
   * dizia 919,00 com 382,60 de credito informado logo ao lado. O Total Geral continua
   * intocado: ele e o valor da VENDA, e e o que sai na nota fiscal (decisao (a)). */
  /* [30/09] O frete informado na guia Transporte tambem entra - pedido do Deivid. No
   * pedido 185 o SubTotal dizia 919,00 e a parcela 934,00: a parcela ja somava os R$ 15
   * de frete (e o que o banco cobra), o SubTotal nao.
   *
   * MESMA regra da previa das parcelas (usePedidoDetalhes) e da FN_FRETE_COBRADO: o frete
   * so e cobrado com modalidade 1 ou com "somar frete" marcado no CIF. Locacao nao soma:
   * la cada parcela e o valor mensal cheio, e a previa tambem nao poe frete. */
  const transporte = values?.transporte;
  const freteCobrado =
    values?.finalidade?.finalidade !== 'Locação'
    && (String(transporte?.mod_frete) === '1' || transporte?.somar_frete_cif === 'S')
      ? (Number(transporte?.valor_frete ?? 0) || 0)
      : 0;

  useEffect(() => {
    /* No formulario tambem: o schema le de la o teto do credito no Salvar - mesmo motivo
       do credito_disponivel, logo abaixo. */
    setFieldValue?.('financeiro.frete_cobrado', freteCobrado);
  }, [freteCobrado]);

  const subTotal = Math.max(0, Number(values?.financeiro?.total_geral ?? 0) + freteCobrado
    - totalDevolucao - Number(values?.financeiro?.valor_credito_utilizado ?? 0));
  /* Math.max: o InputMoney tem allowNegative={false} e mostraria o valor ABSOLUTO de um
     negativo - um -282,60 apareceria na tela como "R$ 282,60", o oposto do que e. */

  /* [29/09] Crédito do cliente.
   *
   * O disponível vem da API e não do saldo bruto: num pedido que já usou crédito, o
   * saldo está debitado, e mostrar só ele faria o operador ver menos do que existe.
   * Refaz a consulta quando o cliente muda - trocar o cliente troca o crédito. */
  const clienteId = values?.cadastro?.cli_id;
  const [creditoDisponivel, setCreditoDisponivel] = useState(0);

  useEffect(() => {
    if (!clienteId) {
      setCreditoDisponivel(0);
      setFieldValue?.('financeiro.credito_disponivel', 0);
      return;
    }

    let ativo = true;

    ClienteController.CreditoDisponivel(clienteId, vendaId).then((resposta) => {
      if (!ativo) return;
      const linha = Array.isArray(resposta.Data) ? resposta.Data[0] : resposta.Data;
      const disponivel = Number(linha?.disponivel ?? 0);
      setCreditoDisponivel(disponivel);
      /* [30/09] Tambem no formulario: e de la que o schema le no Salvar. Aviso na aba
         nao basta - o Deivid informou valor acima do saldo, viu o aviso, e o pedido
         salvou mesmo assim. O aviso so pinta o campo; quem trava o Salvar e o schema. */
      setFieldValue?.('financeiro.credito_disponivel', disponivel);
    });

    return () => { ativo = false; };
  }, [clienteId, vendaId]);

  const creditoUtilizado = Number(values?.financeiro?.valor_credito_utilizado ?? 0);

  /* [30/09] O teto e o MENOR entre o saldo e o valor do pedido.
   *
   * So o saldo nao basta: com R$ 382,60 de saldo e um pedido de R$ 100, o operador
   * podia digitar 382,60 - o banco debitava tudo e o cliente perdia R$ 282,60. "Credito
   * maior que a venda" quer dizer que o SALDO e maior; a venda zera e o resto fica na
   * conta corrente. Nunca consumir mais do que a venda vale. */
  /* [30/09] Com o frete: e o que o banco usa de teto (script 144, FN_VENDA_TOTAL_LIQUIDO).
   * So com o total geral, credito de R$ 934 num pedido de R$ 919 + R$ 15 de frete parava
   * em 919 e deixava R$ 15 a cobrar - a venda nao zerava, contra a regra do card. */
  const totalDoPedido = Number(values?.financeiro?.total_geral ?? 0) + freteCobrado;
  const creditoMaximo = Math.max(0, Math.min(creditoDisponivel, totalDoPedido));

  /* Passar do disponível é o único erro possível aqui, e ele precisa aparecer ANTES do
   * Salvar: a procedure recusa com mensagem, mas o botão está na última aba e o campo
   * fica numa aba cinza atrás. Mesma armadilha do cpg_id obrigatório, em 07/08. */
  const creditoExcedido = creditoUtilizado > creditoMaximo + 0.004;
  // Qual dos dois tetos o operador passou - a mensagem precisa dizer o motivo certo.
  const passouDoPedido = creditoUtilizado > totalDoPedido + 0.004
    && totalDoPedido < creditoDisponivel;

  // [30/09] '6': o Financeiro passou a vir depois do Transporte. O numero e a posicao da aba.
  return (
    <TabPanel value={'6'}>
      <div className={'grid grid-cols-12 pt-4 gap-4 w-5/6'}>
        
        
        <FormControlLabel
          disabled={travado}
          control={
            <Checkbox              
              checked={values?.financeiro?.gerar_financeiro || false}
              name='financeiro.gerar_financeiro'              
              onChange={(event) =>
                setFieldValue(
                  'financeiro.gerar_financeiro',
                  event.target.checked
                )
              }
            />
          }
          className='col-span-2'
          label='Gerar financeiro'
          labelPlacement='end'
        />
        <InputDate
          label='Data de referência'
          name='financeiro.data_referencia'
          className={'col-span-2'}
          disabled={travado}
          handleBlur={handleBlur}
          handleChange={handleChange}
          error={
            touched?.financeiro?.data_referencia &&
              errors?.financeiro?.data_referencia
              ? errors?.financeiro?.data_referencia
              : ''
          }
          hasError={
            !!(
              touched?.financeiro?.data_referencia &&
              errors?.financeiro?.data_referencia
            )
          }
          value={values?.financeiro?.data_referencia ?? ''}
        />
        <InputSelect
            disabled={!values?.financeiro?.gerar_financeiro || travado}
            label='Forma de pagamento'
            name='financeiro.tpg_id'
            size='small'
            items={paymentOptions}
            className={'col-span-5'}
            handleChange={handleChange}
            helperText={
              touched?.financeiro?.tpg_id && errors?.financeiro?.tpg_id
                ? errors?.financeiro?.tpg_id
                : ''
            }
            error={!!(touched?.financeiro?.tpg_id && errors?.financeiro?.tpg_id)}
            value={values?.financeiro?.tpg_id ?? ''}
          />
        <InputSelect
          disabled={!values?.financeiro?.gerar_financeiro || travado}
          label='Bandeira'
          name='financeiro.bandeira'
          size='small'
          items={OPTIONS}
          className={'col-span-3'}
          handleChange={handleChange}
          helperText={
            touched?.financeiro?.bandeira && errors?.financeiro?.bandeira
            ? errors?.financeiro?.bandeira
            : ''
          }
          error={
            !!(touched?.financeiro?.bandeira && errors?.financeiro?.bandeira)
          }
          value={values?.financeiro?.bandeira ?? ''}
        />
          
          
          <InputSelect
            disabled={!values?.financeiro?.gerar_financeiro || travado}
            label='Categoria'
            name='financeiro.categoria'
            size='small'
            items={OPTIONS}
            className={'col-span-2'}
            handleChange={handleChange}
            helperText={
              touched?.financeiro?.categoria && errors?.financeiro?.categoria
                ? errors?.financeiro?.categoria
                : ''
            }
            error={
              !!(touched?.financeiro?.categoria && errors?.financeiro?.categoria)
            }
            value={values?.financeiro?.categoria ?? ''}
          />
          <InputSelect
            disabled={!values?.financeiro?.gerar_financeiro || travado}
            label='Centro de custo'
            name='financeiro.cc_id'
            size='small'
            items={costCenterOptions}
            className={'col-span-2'}
            handleChange={handleChange}
            helperText={
              touched?.financeiro?.cc_id && errors?.financeiro?.cc_id
                ? errors?.financeiro?.cc_id
                : ''
            }
            error={!!(touched?.financeiro?.cc_id && errors?.financeiro?.cc_id)}
            value={values?.financeiro?.cc_id ?? ''}
          />
        {/* A condição só abre depois da forma de pagamento: é ela que define quais
            opções fazem sentido (à vista x a prazo). Com a forma vazia o campo fica
            desabilitado e o texto de apoio diz o que fazer - em vez de deixar o
            usuário escolher um par inválido e descobrir depois. */}
        <InputSelect
          disabled={
            !values?.financeiro?.gerar_financeiro ||
            travado ||
            !temFormaPagamentoSelecionada
          }
          label='Condição de parcelamento'
          name='financeiro.cpg_id'
          size='small'
          items={paymentConditionOptions}
          className={'col-span-5'}
          handleChange={handleChange}
          helperText={
            !temFormaPagamentoSelecionada
              ? 'Informe a forma de pagamento primeiro.'
              : touched?.financeiro?.cpg_id && errors?.financeiro?.cpg_id
                ? errors?.financeiro?.cpg_id
                : ''
          }
          error={!!(touched?.financeiro?.cpg_id && errors?.financeiro?.cpg_id)}
          value={values?.financeiro?.cpg_id ?? ''}
        />

        {/* [REMOVIDO 24/09/2026] O campo "Recebimento único" saiu daqui.

            Ele mudava APENAS a prévia de parcelas desta tela: marcado, a
            PROC_CONDICAOPAGAMENTO_SIMULAR_PARCELAS devolvia uma linha só. Quem cria os
            títulos de verdade é a PROC_CONTAS_RECEBER_BOLETO_GRAVAR, e ela NUNCA leu o
            campo — medido no script 104, que varreu todas as procedures do banco atrás
            de `recebimento_unico` e achou três: a da prévia, a que carrega o pedido e a
            que grava a venda (que só o armazena).

            Ou seja: marcado, a tela prometia uma parcela e o cliente recebia N boletos.
            Prévia que mente é pior que campo ausente.

            A regra que o cliente de fato quer já existe logo acima: forma à vista só
            abre condição de uma vez. A coluna vnd_recebimento_unico continua no banco e
            continua recebendo 'N' — tirar coluna é outro assunto. */}
        <InputMoney
          disabled={true}
          label={'Total líquido de produtos'}
          /* [30/09] col-start-1: comeca linha nova, ao lado dos outros totais. Na linha
             de cima ele completava as 12 colunas depois da condicao de parcelamento, e
             ficava separado dos totais de servico e locacao. */
          className={'col-span-3 col-start-1'}
          name={'financeiro.total_liquido_produtos'}
          handleBlur={handleBlur}
          handleChange={handleChange}
          error={
            touched?.financeiro?.total_liquido_produtos &&
              errors?.financeiro?.total_liquido_produtos
              ? errors?.financeiro?.total_liquido_produtos
              : ''
          }
          hasError={
            !!(
              touched?.financeiro?.total_liquido_produtos &&
              errors?.financeiro?.total_liquido_produtos
            )
          }
          value={values?.financeiro?.total_liquido_produtos ?? ''}
        />
        <InputMoney
          disabled={true}
          label={'Total líquido de serviços'}
          className={'col-span-3'}
          name={'financeiro.total_liquido_servicos'}
          handleBlur={handleBlur}
          handleChange={handleChange}
          error={
            touched?.financeiro?.total_liquido_servicos &&
              errors?.financeiro?.total_liquido_servicos
              ? errors?.financeiro?.total_liquido_servicos
              : ''
          }
          hasError={
            !!(
              touched?.financeiro?.total_liquido_servicos &&
              errors?.financeiro?.total_liquido_servicos
            )
          }
          value={values?.financeiro?.total_liquido_servicos ?? ''}
        />
        <InputMoney
          disabled={true}
          label={'Total líquido de locação'}
          className={'col-span-3'}
          name={'financeiro.total_liquido_locacao'}
          handleBlur={handleBlur}
          handleChange={handleChange}
          error={
            touched?.financeiro?.total_liquido_locacao &&
              errors?.financeiro?.total_liquido_locacao
              ? errors?.financeiro?.total_liquido_locacao
              : ''
          }
          hasError={
            !!(
              touched?.financeiro?.total_liquido_locacao &&
              errors?.financeiro?.total_liquido_locacao
            )
          }
          value={values?.financeiro?.total_liquido_locacao ?? ''}
        />
        <InputMoney
          disabled={true}
          label={'Total geral'}
          className={'col-span-3'}
          name={'financeiro.total_geral'}
          handleBlur={handleBlur}
          handleChange={handleChange}
          error={
            touched?.financeiro?.total_geral && errors?.financeiro?.total_geral
              ? errors?.financeiro?.total_geral
              : ''
          }
          hasError={
            !!(
              touched?.financeiro?.total_geral &&
              errors?.financeiro?.total_geral
            )
          }
          value={values?.financeiro?.total_geral ?? ''}
        />
        {/* [30/09] Frete - leitura. E o valor da guia Transporte quando ele e cobrado do
            cliente (regra no freteCobrado, acima); fora disso mostra zero, que e o que
            entra na conta. Abre a segunda linha: o que se soma ao Total geral vem antes
            do que se desconta dele. */}
        <InputMoney
          disabled={true}
          label={'Frete'}
          className={'col-span-3 col-start-1'}
          name={'financeiro.frete_exibicao'}
          handleBlur={handleBlur}
          handleChange={handleChange}
          error={''}
          hasError={false}
          value={comoTexto(freteCobrado)}
        />
        {/* [29/09] Total Devolução e SubTotal.
            Não são campos do formulário - são leitura, calculada do grid ao lado. Por isso
            value direto, sem name nem handleChange: não há o que o usuário edite aqui e
            não há o que gravar. */}
        <InputMoney
          disabled={true}
          label={'Total devolução'}
          className={'col-span-3'}
          name={'financeiro.total_devolucao'}
          handleBlur={handleBlur}
          handleChange={handleChange}
          error={''}
          hasError={false}
          value={comoTexto(totalDevolucao)}
        />
        {/* [29/09] Crédito do cliente. O disponível é leitura; o utilizado é o único
            campo editável dos dois, e vai no JSON como financeiro.valor_credito_utilizado
            (a PROC_VENDA_GRAVAR lê de lá - script 142).

            Locação não aceita crédito: lá cada parcela vale o valor mensal cheio, e a
            procedure recusa com mensagem própria. */}
        <InputMoney
          disabled={true}
          label={'Crédito disponível'}
          className={'col-span-3'}
          name={'financeiro.credito_disponivel'}
          handleBlur={handleBlur}
          handleChange={handleChange}
          error={''}
          hasError={false}
          value={comoTexto(creditoDisponivel)}
        />
        <InputMoney
          /* Locacao nao aceita credito (cada parcela e o valor mensal cheio) - o banco
             recusa. Melhor nem deixar digitar do que recusar no Salvar. */
          disabled={travado || creditoDisponivel <= 0
            || values?.finalidade?.finalidade === 'Locação'}
          label={'Utilizar crédito'}
          className={'col-span-3'}
          name={'financeiro.valor_credito_utilizado'}
          handleBlur={handleBlur}
          handleChange={handleChange}
          error={creditoExcedido
            ? (passouDoPedido
                ? `O pedido vale ${comoTexto(totalDoPedido)} — não use mais crédito que isso`
                : `Máximo disponível: ${comoTexto(creditoDisponivel)}`)
            : ''}
          hasError={creditoExcedido}
          value={values?.financeiro?.valor_credito_utilizado ?? ''}
        />
        <InputMoney
          disabled={true}
          label={'SubTotal'}
          /* [30/09] Com o Frete a segunda linha encheu; o SubTotal desce e fica na coluna
             do Total geral - o resultado embaixo do valor de onde a conta parte. */
          className={'col-span-3 col-start-10'}
          name={'financeiro.subtotal'}
          handleBlur={handleBlur}
          handleChange={handleChange}
          error={''}
          hasError={false}
          value={comoTexto(subTotal)}
        />
      </div>
      {/* Reprocessar boleto. O motivo vai no tooltip para o usuário entender por que
          está bloqueado - botão cinza sem explicação já gerou chamado aqui.
          O <span> existe porque o Tooltip do MUI não dispara em elemento desabilitado.

          Este é o ÚNICO controle que continua ativo em modo consulta (duplo clique).
          Motivo: o botão Editar da listagem só aparece com a venda em aberto, então
          uma venda fechada só abre por duplo clique - e sem esta exceção não haveria
          como reemitir boleto dela. Não é brecha de edição: o botão não altera a
          venda, e o servidor revalida a regra antes de gerar qualquer coisa. */}
      <div className={'grid grid-cols-8 pt-6'}>
        <Box className='col-span-8 flex justify-end'>
          <Tooltip title={reprocessarBoleto?.motivo ?? ''}>
            <span>
              <Button
                variant='contained'
                color='warning'
                disabled={
                  reprocessandoBoleto || !reprocessarBoleto?.podeReprocessar
                }
                onClick={() => handleReprocessarBoletoVenda?.()}
              >
                {/* Venda que nunca teve boleto usa o mesmo botão para a PRIMEIRA
                    geração - é o caso da venda vinda de Ordem de Serviço. Chamar isso
                    de "Reprocessar" confundiria quem nunca processou nada. */}
                {reprocessandoBoleto
                  ? 'Processando...'
                  : (reprocessarBoleto?.qtdBoletos ?? 0) > 0
                    ? 'Reprocessar boleto'
                    : 'Gerar boleto'}
              </Button>
            </span>
          </Tooltip>
        </Box>
      </div>

      {/* [29/09] As parcelas cedem espaço para o grid de devoluções.
          5/8 e 3/8: as parcelas têm quatro colunas e as devoluções três, e o grid da
          direita é de leitura - não precisa da mesma largura. */}
      <div className={'grid grid-cols-8 gap-7 pt-4'}>
        <Box className='col-span-5'>
          {/* [29/09] Os dois grids ganharam titulo. Lado a lado e sem nome, o da direita
              nao se explica: "68 | 28/09 | R$ 1.200,00" nao diz que sao notas de
              devolucao. O da esquerda ganha o dele por simetria - um titulo so faria o
              outro grid parecer subordinado a ele. */}
          <Typography variant='subtitle2' sx={{ color: '#D91E18', mb: 1 }}>
            Parcelas
          </Typography>
          <DataTable
            height={'35vh'}
            columns={PARCELAS_COLUMNS}
            rows={values?.financeiro?.parcelas ?? []}
            showFilter={false}
            showButtonToXlsx={false}
          />
        </Box>
        <Box className='col-span-3'>
          <Typography variant='subtitle2' sx={{ color: '#D91E18', mb: 1 }}>
            Devoluções
          </Typography>
          <DataTable
            height={'35vh'}
            columns={DEVOLUCAO_COLUMNS}
            rows={notasDevolucao}
            showFilter={false}
            showButtonToXlsx={false}
          />
        </Box>
      </div>
      <div className={'grid grid-cols-8 gap-7 pt-8'}>
        <Box className='col-span-8'>
          <DataTable
            height={'21vh'}
            columns={COLUMNS}
            rows={[]}
            showFilter={false}
            showButtonToXlsx={false}
          />
        </Box>
      </div>
    </TabPanel>
  );
};
